import { css } from '@emotion/css';
import dayjs, { Dayjs } from 'dayjs';

/**
 * 原生 input 支持的时间类型
 */
export type NativeType = 'date' | 'datetime-local' | 'week' | 'month' | 'time';

/**
 * antd 支持的 picker 类型
 */
export type PickerType = 'date' | 'week' | 'month' | 'quarter' | 'year' | 'time';

/**
 * 各原生 input type 对应的 dayjs 格式化字符串
 */
export const FORMAT_MAP: Record<NativeType, string> = {
  date: 'YYYY-MM-DD',
  'datetime-local': 'YYYY-MM-DDTHH:mm:ss',
  week: 'YYYY-[W]ww',
  month: 'YYYY-MM',
  time: 'HH:mm:ss',
};

/**
 * 根据 antd 的 picker 和 showTime 参数，决定原生 input 的 type 值。
 * 对于 quarter / year 等原生不支持的 picker，返回 null，回退到 antd 组件。
 */
export function resolveNativeType(
  picker: PickerType | undefined,
  showTime?: boolean,
): NativeType | null {
  const base = picker || 'date';
  if (base === 'time') {
    return 'time';
  }
  if (base === 'date' && showTime) {
    return 'datetime-local';
  }
  if (base === 'date' || base === 'week' || base === 'month') {
    return base;
  }
  // quarter / year 原生 input 不支持
  return null;
}

/**
 * 将 Dayjs 对象转换为原生 input 所需的字符串值
 */
export function dayjsToInputValue(
  date: Dayjs | null | undefined,
  nativeType: NativeType,
  format?: string,
): string {
  if (!date) return '';
  if (format) return date.format(format);
  return date.format(FORMAT_MAP[nativeType]);
}

/**
 * 将原生 input 的字符串值解析为 Dayjs 对象
 */
export function inputValueToDayjs(value: string): Dayjs | null {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

/**
 * 根据 format 字符串推导原生 input 的 step 步长（秒）
 */
export function parseStep(format?: string): number | undefined {
  if (!format) return undefined;
  if (format.includes('ss')) return 1;
  if (format.includes('mm')) return 60;
  if (format.includes('HH') || format.includes('hh')) return 3600;
  return undefined;
}

/** 窄屏下原生 input 的外层容器样式 */
export const wrapperCss = css({
  display: 'flex',
  gap: 8,
  flexDirection: 'column',
});
